import BaseButton from 'components/atoms/baseButton';
import LinkedCallReadingsModal from 'components/molecules/linked-call-readings-modal';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useLinkReadingsToCall } from 'infrastructure/hooks/rpm';
import { queryKeyBase } from 'infrastructure/hooks/link-call-readings-tool/use-get-data';
import { useQueryClient } from '@tanstack/react-query';
import { useLinkCallReadingsToolFilters } from 'infrastructure/hooks/link-call-readings-tool/use-link-call-readings-tool-filter';
import { formatAuthorName } from 'utils/formatters';

import s from './styles.module.scss';

import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { FC } from 'react';
import type {
  ILinkCallReadingsTool,
  ILinkCallReadingsToolResponse,
} from 'infrastructure/hooks/link-call-readings-tool/types';

interface LinkActionProps {
  callId: string;
  patient: ILinkCallReadingsTool['patient'];
}

const LinkAction: FC<LinkActionProps> = ({ callId, patient }) => {
  const [open, toggleOpen] = useReducer((v) => !v, false);
  const { linkReadingsToCallAsync, isLoading } = useLinkReadingsToCall();
  const { filter } = useLinkCallReadingsToolFilters();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleLinkReadingsToCall = async (readings: IActivityData[]) => {
    const selectedReadingsIds = readings.map((item) => ({
      readingId: item.id,
      deviceId: item.deviceId,
    }));

    await linkReadingsToCallAsync(
      {
        callId,
        readings: selectedReadingsIds,
        patientGuid: patient.guid,
      },
      {
        onSuccess: () => {
          const queryKey = [...queryKeyBase, filter];
          const newReadings: ILinkCallReadingsTool['linkedReadings'] =
            readings.map((item) => ({
              id: item.id,
              createdAt: item.createdAt,
              globalStatus: item.globalStatus,
              readingType: item.readingType,
              thresholdName: item.thresholdName,
              value: item.value,
              statuses: item.statuses,
              isRecovered: item.isRecovered,
              patientThreshold: item.patient.threshold,
              patient: item.patient,
              patientTimezone: item.patient.timezone,
              groupType: item.groupType,
              dateTime: item.dateTime,
              deviceId: item.deviceId,
            }));

          queryClient.setQueryData<ILinkCallReadingsToolResponse>(
            queryKey,
            (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                data: oldData.data.map((item) => {
                  if (item.callId === callId) {
                    return {
                      ...item,
                      linkedReadings: item.linkedReadings.concat(newReadings),
                    };
                  }
                  return item;
                }),
                count: oldData.count,
              };
            },
          );

          toggleOpen();
        },
      },
    );
  };

  return (
    <div className={s.actions}>
      <BaseButton
        label={t('linkedCallReadings.linkReadings')}
        onClick={toggleOpen}
        disabled={open}
        loading={isLoading}
        className={s['action-btn']}
      />
      {open && (
        <LinkedCallReadingsModal
          patientGuid={patient.guid}
          clinicGuid={patient.clinicGuid}
          isOpenConnect={false}
          onLink={handleLinkReadingsToCall}
          open={open}
          toggle={toggleOpen}
          subTitle={formatAuthorName(patient)}
          linkLoading={isLoading}
        />
      )}
    </div>
  );
};
export default LinkAction;
